import {
  CallbackFor,
  ChannelInstanceEntry,
  ChannelInstanceMeta,
  ChannelState,
  CopilotChannelMessage,
  CopilotChannelOptions,
  DataFor,
  MessageLogEntry,
  StateListener,
  TopicName,
  TopicToHandlers,
  UnsubscribeValue,
} from '../types/Channel';
import { uniqueId } from '../utils/uniqueId';

function generateUniqueKeyFor(keyedMap: Record<string, unknown>) {
  let key;
  do {
    key = uniqueId();
  } while (keyedMap[key] !== undefined);

  return key;
}

// By wrapping the callback we get a unique instance of the callback that can be identified later.
// This handles the case of the same function being subscribed multiple times
function createWrappedCallback<Name extends TopicName>(
  callback: CallbackFor<Name>
): CallbackFor<Name> {
  return (data: DataFor<Name>) => {
    return callback(data);
  };
}

type StateSetter = (state: ChannelState) => Partial<ChannelState>;

/**
 * It's highly recommended to never use this class directly, instead you should
 * rely on createCopilotChannel. CopilotChannel uses the BroadcastChannel
 * browser API which is supported in almost every browser but not all of them.
 * Because of this it's better to use the creator function which does an
 * existance check before trying to make the channel.
 */
class CopilotChannel {
  private topicToHandlers: TopicToHandlers;
  private unsubscribeMap: Record<string, UnsubscribeValue>;
  private currentTimeoutIds: Set<number>; // used to clean up timeouts that have been set up on unmount
  private __ENABLE_TEST_MODE: boolean;

  private messageLog: Array<MessageLogEntry<TopicName>>;
  private state: ChannelState;
  private stateListeners: Array<(state: ChannelState) => void>;
  private stateUnsubscribeMap: Record<string, StateListener>;
  private version: number;
  private notifyScheduled = false;

  // Backing storage for the widget-instance registry (see registerInstance).
  private instanceRegistry: Record<string, ChannelInstanceMeta> = {};
  private duplicateInstanceReported = false;

  constructor({ __ENABLE_TEST_MODE }: CopilotChannelOptions = {}) {
    this.topicToHandlers = {};
    this.unsubscribeMap = {};
    this.currentTimeoutIds = new Set();
    this.__ENABLE_TEST_MODE = Boolean(__ENABLE_TEST_MODE);
    this.messageLog = [];
    this.stateListeners = [];
    this.state = {
      appContext: {},
      isCanvasOpen: false,
      isNavCopilotAvailable: false,
      isWidgetActive: false,
      isWidgetOpen: false,
      layoutType: 'panel',
      breezeContext: {
        messageContexts: [],
        context: {},
        turnMetadata: {},
        conversationMetadata: {},
        agentId: undefined,
      },
    };
    this.stateUnsubscribeMap = {};
    this.version = 1;
  }

  addStateListener = (listener: StateListener) => {
    this.stateListeners.push(listener);

    const unsubscribeKey = this.generateUniqueUnsubscribeKey();
    this.stateUnsubscribeMap[unsubscribeKey] = listener;

    return () => {
      return this.removeStateListener(unsubscribeKey);
    };
  };

  getState = () => {
    return this.state;
  };

  setState = (
    partialStateOrStateSetter: Partial<ChannelState> | StateSetter
  ) => {
    if (typeof partialStateOrStateSetter === 'function') {
      const partialState = partialStateOrStateSetter(this.state);
      this.state = { ...this.state, ...partialState };
    } else {
      this.state = { ...this.state, ...partialStateOrStateSetter };
    }
    this.scheduleStateNotify();
  };

  // The state update above is synchronous (getState() reflects it immediately),
  // but the listener notify is deferred to a microtask and coalesced. The only
  // state listener is CopilotChannelProvider's useSyncExternalStore
  // subscription, which is safe to notify asynchronously. Deferring guarantees
  // the React re-render this triggers never runs *inside* a React commit:
  // effect-cleanup callers (useSetAssistantContext / useSetBreezeContext /
  // replicator's Chat useResetCopilotWidgetOnUnmount, and other unmount-time
  // setState callers) fire during React's unmount commit, and a synchronous
  // notify there forces a re-render mid-teardown — which trips the app-wide
  // error boundary in dev and hard-crashes the renderer (EXC_BAD_ACCESS) in prod.
  private scheduleStateNotify = () => {
    if (this.notifyScheduled) {
      return;
    }
    this.notifyScheduled = true;
    queueMicrotask(() => {
      this.notifyScheduled = false;
      this.stateListeners.forEach((listener) => listener(this.state));
    });
  };

  removeStateListener(key: string) {
    const stateListenerToRemove = this.stateUnsubscribeMap[key];
    if (stateListenerToRemove) {
      this.stateListeners = this.stateListeners.filter(
        (cb) => cb !== stateListenerToRemove
      );
    }
    delete this.unsubscribeMap[key];
  }

  getMessageLog = () => {
    return this.messageLog;
  };

  postToTopic = <Name extends TopicName>(topic: Name, data: DataFor<Name>) => {
    const message: CopilotChannelMessage = { payload: data, topic };
    this.deliver(message);
  };

  private deliver = (message: CopilotChannelMessage) => {
    const { payload, topic } = message;

    // Track messages in test mode so we can compare against them in tests
    if (this.__ENABLE_TEST_MODE) {
      this.messageLog.push({ data: payload, topic });
    }

    // We're intentionally broadening type check here because we lose type
    // safety when we go over the broadcast channel. The hooks that add handlers
    // ensure they have type safety so we don't need to do it here.
    const topicHandlers = (this.topicToHandlers[topic] || []) as Function[];

    topicHandlers.forEach((handler) => {
      setTimeout(() => {
        handler(payload);
      }, 0);
    });
  };

  generateUniqueUnsubscribeKey(): string {
    return generateUniqueKeyFor(this.unsubscribeMap);
  }

  unsubscribe(key: string) {
    const unsubscribeData = this.unsubscribeMap[key];
    if (unsubscribeData) {
      const { callback, topic } = unsubscribeData;
      const currentCallbacks = this.topicToHandlers[topic] as Function[];
      if (topic && currentCallbacks) {
        const filteredCallbacks = currentCallbacks.filter(
          (cb) => cb !== callback
        );

        if (filteredCallbacks.length) {
          // This is just a filtered list of callbacks, typescript has a hard
          // time here with type narrowing but since we're just filtering out
          // callbacks it's a safe change
          this.topicToHandlers[topic] = filteredCallbacks as any;
        } else {
          delete this.topicToHandlers[topic];
        }
      }
    }
    delete this.unsubscribeMap[key];
  }

  subscribe<Name extends TopicName>(topic: Name, callback: CallbackFor<Name>) {
    const wrappedCallback = createWrappedCallback(callback);

    const topicHandlers: Array<CallbackFor<Name>> =
      this.topicToHandlers[topic] || (this.topicToHandlers[topic] = []);
    topicHandlers.push(wrappedCallback);

    const unsubscribeKey = this.generateUniqueUnsubscribeKey();
    this.unsubscribeMap[unsubscribeKey] = {
      topic,
      callback: wrappedCallback,
    };

    return () => {
      return this.unsubscribe(unsubscribeKey);
    };
  }

  // Widget-instance registry: lets a consumer detect when more than one widget
  // has joined this (shared, top-window) channel. Exposed as methods rather than
  // public fields because the channel is a singleton returned by whichever
  // toolkit bundle created it first — a consumer must be able to feature-detect
  // (`typeof channel.registerInstance === 'function'`) and degrade on an older
  // instance that predates these methods instead of crashing on a missing field.
  registerInstance = (
    instanceKey: string,
    meta: ChannelInstanceMeta
  ): (() => void) => {
    this.instanceRegistry[instanceKey] = meta;
    return () => {
      delete this.instanceRegistry[instanceKey];
    };
  };

  getRegisteredInstances = (): ChannelInstanceEntry[] =>
    Object.keys(this.instanceRegistry).map((instanceKey) => ({
      instanceKey,
      ...this.instanceRegistry[instanceKey],
    }));

  hasReportedDuplicateInstance = () => this.duplicateInstanceReported;

  markDuplicateInstanceReported = () => {
    this.duplicateInstanceReported = true;
  };

  getVersion = () => {
    return this.version;
  };

  // This should never be used in production, it's a testing util to make sure
  // we can test backwards compatibility. The version for the Channel is set
  // based on breaking changes to the channel and should never be altered
  // outside of that.
  __FOR_TESTING_ONLY_setVersion = (version: number) => {
    this.version = version;
  };

  size = () => {
    return Object.entries(this.topicToHandlers).filter(
      ([__k, v]) => v && v.length > 0
    ).length;
  };

  cleanup = () => {
    this.currentTimeoutIds.forEach((timeoutId) => clearTimeout(timeoutId));
    this.messageLog = [];
    this.instanceRegistry = {};
    this.duplicateInstanceReported = false;
  };
}

export default CopilotChannel;
